<template>
  <div class="customer-table table-component">
    <artisio-table :pagination="tableData.pagination" :sort="tableData.ordering" :items="tableData.rows"
                   :busy="tableData.loading" :fields="tableFields" show-toggle-columns
                   :checkboxes="true" :search="tableData.search" export-vuex-module-name="customer"
                   export-permission-name="export_customer" :export-attributes="exportAttributes"
                   :enable-simple-search="false" @paramsChanged="getTableData" :selectable="selectable"
                   @selectedItemsChange="onSelectedItemsChange" filter-toggle-string="customer-table"
                   :extra-data="extraData" @update-toggle-columns="updateToggleColumns"
                   :enable-frontend-export="enableFrontendExport" requires-min-height
                   @on-column-save="updateToggleColumns" :enable-export="enableExport">
      <template v-slot:search-form-placeholder>
        <search-collapse table-id="customer-table">
          <customer-search :exclude-search-fields="excludeSearchFields"/>
        </search-collapse>
      </template>
      <template v-if="tableSource === SOURCE_AUDIENCE_CUSTOMERS" v-slot:header-buttons="{selectedItems}">
        <b-dropdown :text="$t('Actions')" variant="primary" v-permission="'change_audience'">
          <b-dropdown-item @click="onUnsubscribeCustomersFromAudienceClick(selectedItems)"
                           :disabled="Array.from(selectedItems).every(item => !item.subscribed)">
            {{ $t('Unsubscribe From Audience') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onRemoveCustomersFromAudienceClick(selectedItems)"
                           :disabled="selectedItems.size === 0">
            {{ $t('Remove From Audience') }}
          </b-dropdown-item>
        </b-dropdown>
      </template>
      <template v-else v-slot:header-buttons="{selectedItems}">
        <b-dropdown :text="$t('Actions')" variant="primary" v-permission="'change_customer'">
          <bank-details-bulk-upload/>
        </b-dropdown>
        <b-button v-if="hasDataCollectionProvider" variant="primary" v-permission="'change_auction'"
                  :disabled="tableData.loading" @click="onSyncClick"
                  :title="$t('Following button will synchronize live data from fulcrum')">
          {{ $t('Sync with Fulcrum') }}
        </b-button>
      </template>
      <template slot="cell(no)" slot-scope="{index, item: customer}">
        <div class="d-flex justify-content-between">
          <span>{{ customer.customer_no }}</span>
          <red-flag-displayer :customer="customer"/>
        </div>
        <action-buttons v-if="!hideActions" :view-permission="'view_customer'"
                        :edit-permission="'change_customer'"
                        :delete-permission="'delete_customer'"
                        @viewClick="onViewClick(customer)"
                        @editClick="onEditClick(customer)"
                        @deleteClick="onRemoveClick(customer)"/>
      </template>
      <template slot="cell(full_name)" slot-scope="{item: customer}">
        {{ customer.display_name }}
      </template>
      <template slot="cell(email)" slot-scope="{item: customer}">
        <template v-if="customer.email">
          <span class="badge border mr-1">
            <a :href="`mailto:` + customer.email"> {{ customer.email }}</a>
          </span>
        </template>
      </template>
      <template slot="cell(phone)" slot-scope="{item: customer}">
        <template v-if="customer.phone">
          <span class="badge border mr-1" v-for="(phone, index) in customer.phone.split(',')"
                :key="`grid-phone-${index}`">
            {{ phone }}
          </span>
        </template>
      </template>
      <template slot="cell(address)" slot-scope="{item: customer}">
        <div v-if="customer.display_billing_address"
             v-html="$options.filters.splitAddressForMultiline(customer.display_billing_address)">
        </div>
      </template>
      <template slot="cell(status)" slot-scope="{item: customer}">
        <span v-as-status="customer.status">
          {{ customer.status | removeUnderscores | toUpperCaseFirst | translate }}
        </span>
      </template>
      <template slot="cell(type)" slot-scope="{item: customer}">
        {{ customer.type | removeUnderscores | toUpperCaseFirst }}
      </template>
      <template slot="cell(activity)" slot-scope="{item: customer}">
        <p class="mb-1" v-if="customer.activity && customer.activity.buyer.length > 0">
          <b class="mr-2">{{ $t('Buyer:') }}</b>
          <span class="badge border mr-1" v-for="(obj, index) in customer.activity.buyer"
                :key="`buyer-category-badge-${index}`">
            {{ obj.name }} ({{ obj.total_count }}) - {{ obj.total_price | asCurrency }}
          </span>
        </p>
        <p class="mb-1" v-if="customer.activity && customer.activity.vendor.length > 0">
          <b class="mr-2">{{ $t('Vendor:') }}</b>
          <span class="badge border mr-1" v-for="(obj, index) in customer.activity.vendor"
                :key="`vendor-category-badge-${index}`">
            {{ obj.name }} ({{ obj.total_count }}) - {{ obj.total_price | asCurrency }}
          </span>
        </p>
      </template>
      <template slot="cell(account_managers)" slot-scope="{item: customer}">
        <template v-if="customer.account_managers && customer.account_managers.length > 0">
          {{ customer.account_managers[0].display_name }}
        </template>
      </template>
      <template slot="cell(optin)" slot-scope="{item: customer}">
        <span v-html="$options.filters.asBadgeBoolean(customer.optin)"/>
      </template>
      <template slot="cell(gender)" slot-scope="{item: customer}">
        {{ customer.gender | toUpperCaseFirst }}
      </template>
      <template slot="cell(enable_estimate_request)" slot-scope="{item: customer}">
        <span v-html="$options.filters.asBadgeBoolean(customer.enable_estimate_request)"/>
      </template>
      <template slot="cell(is_company)" slot-scope="{item: customer}">
        <span v-html="$options.filters.asBadgeBoolean(customer.is_company)"/>
      </template>
      <template slot="cell(sms_optin)" slot-scope="{item: customer}">
        <span v-html="$options.filters.asBadgeBoolean(customer.sms_optin)"/>
      </template>
      <template slot="cell(mark_optin)" slot-scope="{item: customer}">
        <span v-html="$options.filters.asBadgeBoolean(customer.mark_optin)"/>
      </template>
      <template slot="cell(is_vendor)" slot-scope="{item: customer}">
        <span v-html="$options.filters.asBadgeBoolean(customer.is_vendor)"/>
      </template>
      <template slot="cell(client_tier)" slot-scope="{item: customer}">
        <client-tier-display-widget :customer="customer" />
      </template>
      <template slot="cell(created_data)" slot-scope="{item: customer}">
        {{ customer.created_at | toDate }} by {{ customer.created_by.display_name }}
      </template>
      <template slot="cell(updated_data)" slot-scope="{item: customer}">
        {{ customer.updated_at | toDate }} by {{ customer.updated_by.display_name }}
      </template>
      <template slot="cell(shipping_address)" slot-scope="{item: customer}">
        <div v-if="customer.display_shipping_address"
             v-html="$options.filters.splitAddressForMultiline(customer.display_shipping_address)">
        </div>
      </template>
      <template slot="cell(tags)" slot-scope="{item: customer}">
        <div v-if="customer.tags">
          <span class="badge badge-info mr-2" v-for="tag in customer.tags.split(',')"
                :key="`customer-table-tag-${customer.uuid}-${tag}`">{{ tag }}</span>
        </div>
      </template>
      <template slot="cell(trust_level)" slot-scope="{item: customer}">
        {{ getTrustLevelName(customer.trust_level ? customer.trust_level.trust_level_uuid : null) }}
      </template>
      <template slot="cell(birth_date)" slot-scope="{item: customer}">
        {{ customer.birth_date | toDate }}
      </template>
      <template slot="cell(credit_card_verified_at)" slot-scope="{item: customer}">
        {{ customer.credit_card_verified_at | toDateTime }}
      </template>

      <template slot="cell(personal_identification_number)" slot-scope="{item: customer}">
        <template v-if="customer.identification">
          {{ customer.identification.personal_identification_number }}
        </template>
      </template>

      <template slot="cell(document_type)" slot-scope="{item: customer}">
        <template v-if="customer.identification">
          {{ customerIdentificationTypesMap[customer.identification.document_type] }}
        </template>
      </template>

      <template slot="cell(document_number)" slot-scope="{item: customer}">
        <template v-if="customer.identification">
          {{ customer.identification.document_number }}
        </template>
      </template>

      <template slot="cell(document_issue_date)" slot-scope="{item: customer}">
        <template v-if="customer.identification">
          {{ customer.identification.document_issue_date }}
        </template>
      </template>

      <template slot="cell(document_expiry_date)" slot-scope="{item: customer}">
        <template v-if="customer.identification">
          {{ customer.identification.document_expiry_date }}
        </template>
      </template>

      <template slot="cell(date_of_birth)" slot-scope="{item: customer}">
        <template v-if="customer.identification">
          {{ customer.identification.date_of_birth }}
        </template>
      </template>

      <template slot="cell(citizenship_code)" slot-scope="{item: customer}">
        <template v-if="customer.identification">
          {{ countryMap[customer.identification.citizenship_code] }}
        </template>
      </template>

      <template slot="cell(residency_code)" slot-scope="{item: customer}">
        <template v-if="customer.identification">
          {{ countryMap[customer.identification.residency_code] }}
        </template>
      </template>

      <template slot="cell(country_of_birth_code)" slot-scope="{item: customer}">
        <template v-if="customer.identification">
          {{ countryMap[customer.identification.country_of_birth_code] }}
        </template>
      </template>

      <template slot="cell(place_of_issue)" slot-scope="{item: customer}">
        <template v-if="customer.identification">
          {{ customer.identification.place_of_issue }}
        </template>
      </template>

      <template slot="cell(company_reg_number)" slot-scope="{item: customer}">
        <template v-if="customer.identification">
          {{ customer.identification.company_reg_number }}
        </template>
      </template>

      <template slot="cell(vendor_lead_status)" slot-scope="{item: customer}">
        {{ customer.vendor_lead_status | removeUnderscores | toUpperCaseEveryWord | translate }}
      </template>

      <template slot="cell(buyer_lead_status)" slot-scope="{item: customer}">
        {{ customer.buyer_lead_status | removeUnderscores | toUpperCaseEveryWord | translate }}
      </template>

      <template slot="cell(identity_verification)" slot-scope="{item: customer}">
        {{customer.term ? customer.term.identity_verification : '' | toUpperCaseFirst}}
      </template>

      <template slot="cell(kyc_status_verification)" slot-scope="{item: customer}">
        {{customer.term ? customer.term.kyc_status_verification : '' | toUpperCaseFirst}}
      </template>

      <template :slot="`cell(${dynamicField})`" slot-scope="{item, field}"
                v-for="dynamicField in toggleColumns.dynamicFields">
        <span v-if="item.identification && item.identification.dynamic_fields && item.identification.dynamic_fields[dynamicField]"
          v-html="$options.filters.asDynamicField(item.identification.dynamic_fields[dynamicField], field.type, field.choices)">
        </span>
      </template>
      <template slot="cell(exclude_from_auto_payments)" slot-scope="{item: customer}">
        <span v-html="$options.filters.asBadgeBoolean(customer.term.exclude_from_auto_payments)"/>
      </template>

      <template slot="cell(subscribed)" slot-scope="{item: customer}">
        <span v-html="$options.filters.asBadgeBoolean(customer.subscribed)"/>
      </template>

    </artisio-table>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "../../core/components/ams-table/ArtisioTable";
  import {
    CUSTOMER_TABLE_NAME, SOURCE_CUSTOMER_TABLE, WS_DATA_COLLECTION_PROVIDER_PROGRESS, SOURCE_AUDIENCE_CUSTOMERS
  } from "../constants";
  import ActionButtons from "../../core/components/ams-table/ActionButtons";
  import CustomerSearch from "./CustomerSearch";
  import SearchCollapse from "../../core/components/search-collapse/SearchCollapse";
  import i18n from "../../shared/i18n";
  import InventoryService from "../inventory/InventoryService";
  import {ProgressMixin} from "../../core/mixins/progress.mixin";
  import _ from "lodash";
  import {Formatter} from "../../core/services/formatter.service";
  import BankDetailsBulkUpload from "@/modules/customer/BankDetailsBulkUpload";
  import ClientTierDisplayWidget from "@/core/widgets/ClientTierDisplayWidget.vue";
  import RedFlagDisplayer from "@/modules/customer/RedFlagDisplayer.vue";

  const {mapState, mapActions} = createNamespacedHelpers('customer');
  const {
    mapState: globalMapState,
    mapActions: globalMapActions,
    mapGetters: globalMapGetters
  } = createNamespacedHelpers('global');
  const {mapActions: audienceMapActions} = createNamespacedHelpers('marketing/audience')

  export default {
    name: "CustomerTable",
    components: {
      RedFlagDisplayer,
      ClientTierDisplayWidget,
      BankDetailsBulkUpload, SearchCollapse, CustomerSearch, ActionButtons, ArtisioTable},
    props: {
      hideActions: {
        type: Boolean,
        default: false
      },
      excludeSearchFields: {
        type: Array,
        default: () => []
      },
      excludeColumns: {
        type: Array,
        default: () => []
      },
      selectable: {
        type: Boolean,
        default: false
      },
      withInitialData: {
        type: Boolean,
        default: true
      },
      enableExport: {
        type: Boolean,
        default: false
      },
      exportAttributes: {
        type: Object,
        default: () => {
          return {
            customer_no: i18n.t('No'),
            display_name: i18n.t('Full Name'),
            company_name: i18n.t('Company Name'),
            email: i18n.t('Email'),
            phone: i18n.t('Phone'),
            display_billing_address: i18n.t('Address'),
            status: i18n.t('status'),
            progress: null
          }
        }
      },
      enableFrontendExport: {
        type: Boolean,
        default: false
      },
    },
    mixins: [ProgressMixin],
    data: () => {
      return {
        staticExportAttributes: {
          customer_no: i18n.t('No'),
          display_name: i18n.t('Full Name'),
          company_name: i18n.t('Company Name'),
          email: i18n.t('Email'),
          phone: i18n.t('Phone'),
          display_billing_address: i18n.t('Address'),
          status: i18n.t('status'),
          progress: null
        },
        SOURCE_AUDIENCE_CUSTOMERS,
      }
    },
    computed: {
      ...mapState({
        tableData: state => state.tableData[state.tableSource],
        toggleColumns: state => state.tableData[state.tableSource].toggleColumns,
        tableSource: state => state.tableSource,
      }),
      ...globalMapState({
        enableContinuousAuction: state => state.enableContinuousAuction,
        hasDataCollectionProvider: state => state.data_collection_provider_info.has_data_collection_provider,
        countries: state => state.countries,
        customerIdentificationTypes: state => state.customerIdentificationTypes,
        trustLevels: state => state.currentBranch.trust_levels ? state.currentBranch.trust_levels.sort((a, b) => {
          if (a.credit_limit > b.credit_limit) {
            return 1;
          } else if (b.credit_limit > a.credit_limit) {
            return -1;
          }
          return 0;
        }).map((trustLevel) => {
          return {
            uuid: trustLevel.uuid,
            name: `${trustLevel.name} | Limit(${Formatter.asCurrency(trustLevel.credit_limit)}) | Threshold(${Formatter.asCurrency(trustLevel.threshold)})`,
            value: trustLevel.uuid,
            exclude_from_auto_assignment: trustLevel.exclude_from_auto_assignment,
            credit_limit: trustLevel.credit_limit
          }
        }) : []
      }),
      customerIdentificationTypesMap: state => {
        return Object.fromEntries(state.customerIdentificationTypes.map(c => ([c.value, c.key])))
      },
      ...globalMapGetters(['countryMap']),
      tableFields() {
        return _.cloneDeep(this.toggleColumns.fields)
          .filter(field => !this.excludeColumns.includes(field.key) && field.isVisible)
      },
      extraData() {
        return {
          ...this.toggleColumns,
          excludeColumns: this.excludeColumns,
          tableName: `${CUSTOMER_TABLE_NAME}.${this.tableSource}`,
        }
      }
    },
    methods: {
      ...mapActions(['getTableData', 'showModal', 'removeCustomer', 'getModalObject', 'updateToggleColumns']),
      ...globalMapActions(['addProgress', 'removeProgress', 'updateProgressValue']),
      ...audienceMapActions(['unsubscribeCustomersFromAudience', 'removeCustomersFromAudience']),
      onEditClick(item) {
        this.showModal()
        this.getModalObject({uuid: item.uuid, source: SOURCE_CUSTOMER_TABLE})
      },
      onViewClick(customer) {
        this.$router.push({
          name: 'customers.view',
          params: {id: customer.uuid},
        });
      },
      async onRemoveClick(customer) {
        this.$confirm(
          this.$i18n.t("Are you sure you want to delete following customer?"),
          this.$i18n.t('Customer was successfully deleted'),
          this.removeCustomer,
          customer
        );
      },
      onSelectedItemsChange(selectedItems) {
        this.$emit('selectedItemsChange', selectedItems);
      },
      async onSyncClick() {
        const {success} = await InventoryService.syncInventoryItemsWithThirdParty();
        if (success) {
          await this.addProgressIfNotExist('Data collection started');
        }
      },
      async onProgressNotificationReceive(data) {
        await this.handleProgressNotification(data,
          'Data Collection successfully finished',
          'Validation Error',
          'Something went wrong while running data collection. Please, try again later.');
      },
      getTrustLevelName(trust_level_uuid) {
        if (!trust_level_uuid) {
          return '';
        }
        const trustLevel = this.trustLevels.find(tr => tr.uuid === trust_level_uuid);
        if (!trustLevel) {
          return '';
        }
        return trustLevel.name;
      },
      async onUnsubscribeCustomersFromAudienceClick(selectedItems) {
        let res = await this.unsubscribeCustomersFromAudience(selectedItems);
        if (res.success) {
          this.getTableData();
        } else {
          this.$toast('danger', this.$i18n.t('Unable to unsubscribe customers'));
        }
      },
      async onRemoveCustomersFromAudienceClick(selectedItems) {
        let res = await this.removeCustomersFromAudience(selectedItems);
        if (res.success) {
          this.getTableData();
        } else {
          this.$toast('danger', this.$i18n.t('Unable to remove customers'));
        }
      },
    },
    mounted() {
      if (this.withInitialData) {
        this.getTableData();
      }
      this.$ws.listen(`customers.${WS_DATA_COLLECTION_PROVIDER_PROGRESS}`, this.handleProgressNotification);
    },
    beforeDestroy() {
      this.removeProgressIfExist();
      this.$ws.remove(`customers.${WS_DATA_COLLECTION_PROVIDER_PROGRESS}`);
    }
  }
</script>

<style lang="scss" scoped>


  .artisio-table {
    /deep/ {
      .no-col {
        min-width: 160px;
      }

      .fullname-col {
        min-width: 130px;
      }

      .company-name-col {
        min-width: 130px;
      }

      .email-col {
        min-width: 150px;

      }

      .address-col {
        min-width: 250px;
      }

      .phone-col {
        min-width: 100px;
      }

      .status-col {
        min-width: 100px;
      }

      .activity-col {
        min-width: 100px;
      }

      .account-managers-col {
        min-width: 140px;
      }

      .is-company-col {
        min-width: 100px;
      }

      .sms-optin-col {
        min-width: 100px;
      }

      .mark-optin-col {
        min-width: 100px;
      }

      .personal-id-col {
        min-width: 100px;
      }

      .shipping-address-col {
        min-width: 100px;
      }

      .enable-estimate-request-col {
        min-width: 100px;
      }

      .created-data-col {
        min-width: 100px;
      }

      .updated-data-col {
        min-width: 100px;
      }

      .tags-col {
        min-width: 100px;
      }

      .interest-categories-col {
        min-width: 100px;
      }

      .optin-col {
        min-width: 100px;
      }

      .is-vendor-col {
        min-width: 100px;
      }

      .birth-date-col {
        min-width: 100px;
      }
    }
  }




</style>

